/**
 * Main module entities for the Color Explorer tool
 * These are shared across presentation, domain hooks and API layers (if added later).
 */

/**
 * Supported color formats for input and copy actions.
 */
export enum ColorFormat {
  HEX = "hex",
  RGB = "rgb",
  HSL = "hsl",
}

/**
 * Represents a single color in the palette with its current format and value.
 * - id: stable identifier for UI lists and operations
 * - value: string representation corresponding to `format`
 * - format: one of HEX, RGB, HSL
 * - hex: canonical hex value (always stored for contrast math); includes leading '#', e.g., "#112233"
 * - isValid: client-side validation status for the `value`
 */
export interface ColorItem {
  id: string;
  value: string;
  format: ColorFormat;
  hex: string;
  isValid: boolean;
}

/**
 * WCAG status for a matrix cell.
 * - AA: passes AA
 * - AAA: passes AAA (and implicitly AA)
 * - FAIL: fails AA
 */
export enum ContrastBadgeStatus {
  AA = "aa",
  AAA = "aaa",
  FAIL = "fail",
}

/**
 * One cell in the contrast matrix for a specific bg/fg pair.
 * - bgId, fgId reference ColorItem ids.
 * - ratio: numeric contrast ratio (e.g., 4.5). Stored with reasonable precision.
 * - status: AA/AAA/FAIL computed from ratio thresholds.
 */
export interface ContrastCell {
  bgId: string;
  fgId: string;
  ratio: number;
  status: ContrastBadgeStatus;
}

/**
 * Precomputed contrast matrix for all non-identical pairs.
 * The order of ids matches the current palette order.
 */
export interface ContrastMatrix {
  colorIds: string[];
  cells: ContrastCell[]; // flattened list; consumers can index or group by bgId/fgId
}

/**
 * Currently selected pair for the preview panel.
 */
export interface SelectedPair {
  bgId: string;
  fgId: string;
}

/**
 * Encoded share state for a palette.
 * - url: full shareable URL (including query string encoding the palette)
 * - encoded: the encoded palette string placed in the URL
 */
export interface PaletteShareState {
  url: string;
  encoded: string;
}